/**
 * Douglas–Peucker for polygon rings, in whatever plane the coordinates are in. For layers whose
 * outlines carry far more detail than a map can show (an MLS crown runs to thousands of
 * vertices), read once, in the parse worker.
 */
import type { Geometry, Position } from 'geojson';

function segmentDistance2(p: Position, a: Position, b: Position): number {
  const [px, py] = [p[0]!, p[1]!];
  const [ax, ay] = [a[0]!, a[1]!];
  const dx = b[0]! - ax;
  const dy = b[1]! - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const x = ax + t * dx - px;
  const y = ay + t * dy - py;
  return x * x + y * y;
}

/** The ring with every vertex dropped that lies within `tolerance` of the simplified outline; iterative, so a long ring cannot overflow the stack. */
export function simplifyRing(ring: Position[], tolerance: number): Position[] {
  if (ring.length <= 4 || tolerance <= 0) return ring;
  const keep = new Uint8Array(ring.length);
  keep[0] = 1;
  keep[ring.length - 1] = 1;
  const tol2 = tolerance * tolerance;
  // A closed ring starts and ends on one point; its farthest vertex splits it into two open runs.
  let far = 0;
  let farD = -1;
  for (let i = 1; i < ring.length - 1; i++) {
    const d = segmentDistance2(ring[i]!, ring[0]!, ring[0]!);
    if (d > farD) [far, farD] = [i, d];
  }
  keep[far] = 1;
  const stack: [number, number][] = [
    [0, far],
    [far, ring.length - 1],
  ];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let index = -1;
    let max = tol2;
    for (let i = first + 1; i < last; i++) {
      const d = segmentDistance2(ring[i]!, ring[first]!, ring[last]!);
      if (d > max) [index, max] = [i, d];
    }
    if (index >= 0) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }
  return ring.filter((_, i) => keep[i]);
}

/**
 * A polygon or multipolygon simplified ring by ring. A hole that collapses below four points
 * is dropped; an outer ring that would is kept as it was. Other geometries come back as they are.
 */
export function simplifyGeometry(g: Geometry, tolerance: number): Geometry {
  const polygon = (rings: Position[][]): Position[][] =>
    rings.flatMap((ring, i) => {
      const simple = simplifyRing(ring, tolerance);
      if (simple.length >= 4) return [simple];
      return i === 0 ? [ring] : [];
    });
  if (g.type === 'Polygon') return { type: 'Polygon', coordinates: polygon(g.coordinates) };
  if (g.type === 'MultiPolygon') return { type: 'MultiPolygon', coordinates: g.coordinates.map(polygon) };
  return g;
}

/** Metres in degrees of latitude, for a tolerance on a table that is already geographic. */
export const METRES_PER_DEGREE = 111_320;
